import React, { useState, useEffect } from 'react';
import { taskAPI, userAPI, clientAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { Clock, User, Calendar, Filter, Download, TrendingUp, StopCircle } from 'lucide-react';
import { formatDateTime } from '../utils/dateFormat';

const TimeTracking = () => {
  const { isPartner, isManager } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Filters
  const [filterUser, setFilterUser] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('all'); // all, today, week, month, custom

  // Update current time every second for live counting
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Set default date range based on period selection
  const handlePeriodChange = (period) => {
    setFilterPeriod(period);
    const today = new Date();
    
    if (period === 'today') {
      const dateStr = today.toISOString().split('T')[0];
      setFilterDateFrom(dateStr);
      setFilterDateTo(dateStr);
    } else if (period === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(today.getDate() - 7);
      setFilterDateFrom(weekAgo.toISOString().split('T')[0]);
      setFilterDateTo(today.toISOString().split('T')[0]);
    } else if (period === 'month') {
      const monthAgo = new Date(today);
      monthAgo.setMonth(today.getMonth() - 1);
      setFilterDateFrom(monthAgo.toISOString().split('T')[0]);
      setFilterDateTo(today.toISOString().split('T')[0]);
    } else if (period === 'all') {
      setFilterDateFrom('');
      setFilterDateTo('');
    }
    // 'custom' keeps the current values
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tasksRes, usersRes, clientsRes] = await Promise.all([
        taskAPI.getAll(),
        userAPI.getAll(),
        clientAPI.getAll(),
      ]);
      setTasks(tasksRes.data);
      setUsers(usersRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Force stop any running work session (Partner only)
  const handleForceStop = async (taskId, userName) => {
    if (!window.confirm(`Force stop ${userName}'s work session?`)) {
      return;
    }
    try {
      const res = await taskAPI.forceStopWork(taskId);
      alert(res.data.message);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to force stop work');
    }
  };

  // Filter tasks that have time entries
  const tasksWithTime = tasks.filter(task => 
    task.time_entries && task.time_entries.length > 0
  );

  // Apply filters including date range
  const filteredTasks = tasksWithTime.map(task => {
    // Filter time entries by date if date range is set
    let filteredEntries = task.time_entries;
    
    if (filterDateFrom || filterDateTo) {
      filteredEntries = task.time_entries.filter(entry => {
        if (!entry.start_time) return false;
        const entryDate = new Date(entry.start_time).toISOString().split('T')[0];
        if (filterDateFrom && entryDate < filterDateFrom) return false;
        if (filterDateTo && entryDate > filterDateTo) return false;
        return true;
      });
    }

    // Filter by user
    if (filterUser) {
      filteredEntries = filteredEntries.filter(entry => entry.user_id === filterUser);
    }

    return { ...task, time_entries: filteredEntries };
  }).filter(task => {
    // Remove tasks with no matching entries
    if (task.time_entries.length === 0) return false;
    // Filter by client
    if (filterClient && task.client_id !== filterClient) return false;
    return true;
  });

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  // Calculate duration for an entry (live for in-progress entries)
  const getEntryDuration = (entry) => {
    if (entry.end_time) {
      // Completed entry - use stored duration
      return entry.duration_minutes || 0;
    } else {
      // In-progress entry - calculate live duration
      const startTime = new Date(entry.start_time);
      const durationMs = currentTime - startTime;
      return Math.max(0, Math.floor(durationMs / 60000)); // Convert to minutes
    }
  };

  // Calculate time statistics with live counting
  const calculateStats = () => {
    let totalMinutes = 0;
    const userStats = {};
    const clientStats = {};

    filteredTasks.forEach(task => {
      task.time_entries.forEach(entry => {
        const duration = getEntryDuration(entry);
        totalMinutes += duration;

        // User stats
        if (!userStats[entry.user_id]) {
          userStats[entry.user_id] = { name: entry.user_name, minutes: 0 };
        }
        userStats[entry.user_id].minutes += duration;

        // Client stats
        if (!clientStats[task.client_id]) {
          clientStats[task.client_id] = { name: task.client_name, minutes: 0 };
        }
        clientStats[task.client_id].minutes += duration;
      });
    });

    return { totalMinutes, userStats, clientStats };
  };

  const stats = calculateStats();

  // Export to CSV
  const handleExport = () => {
    const rows = [['Task', 'Client', 'User', 'Start Time', 'End Time', 'Duration (mins)']];
    
    filteredTasks.forEach(task => {
      task.time_entries.forEach(entry => {
        rows.push([
          task.task_type_name || task.task_name,
          task.client_name,
          entry.user_name,
          entry.start_time,
          entry.end_time || 'In Progress',
          getEntryDuration(entry),
        ]);
      });
    });

    const csvContent = rows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `time_tracking_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Time Tracking Reports</h2>
        <button
          onClick={handleExport}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          data-testid="export-time-report"
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <Clock className="w-10 h-10 text-blue-600 mr-4" />
            <div>
              <p className="text-sm text-gray-500">Total Time Tracked</p>
              <p className="text-2xl font-bold text-blue-600">{formatTime(stats.totalMinutes)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <User className="w-10 h-10 text-green-600 mr-4" />
            <div>
              <p className="text-sm text-gray-500">Active Users</p>
              <p className="text-2xl font-bold text-green-600">{Object.keys(stats.userStats).length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <TrendingUp className="w-10 h-10 text-purple-600 mr-4" />
            <div>
              <p className="text-sm text-gray-500">Tasks Tracked</p>
              <p className="text-2xl font-bold text-purple-600">{filteredTasks.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="flex items-center mb-3">
          <Filter className="w-5 h-5 mr-2 text-gray-500" />
          <h3 className="font-semibold">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Period</label>
            <select
              value={filterPeriod}
              onChange={(e) => handlePeriodChange(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">From Date</label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => {
                setFilterDateFrom(e.target.value);
                setFilterPeriod('custom');
              }}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">To Date</label>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => {
                setFilterDateTo(e.target.value);
                setFilterPeriod('custom');
              }}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">User</label>
            <select
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">All Users</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>{user.full_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Client</label>
            <select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">All Clients</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>
        </div>
        {(filterDateFrom || filterDateTo || filterUser || filterClient) && (
          <div className="mt-3 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {filterDateFrom && filterDateTo && `Period: ${filterDateFrom} to ${filterDateTo}`}
              {filterDateFrom && !filterDateTo && `From: ${filterDateFrom}`}
              {!filterDateFrom && filterDateTo && `To: ${filterDateTo}`}
            </p>
            <button
              onClick={() => {
                setFilterPeriod('all');
                setFilterDateFrom('');
                setFilterDateTo('');
                setFilterUser('');
                setFilterClient('');
              }}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* User Time Summary */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <h3 className="font-semibold mb-4">Time by User</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(stats.userStats).map(([userId, data]) => (
            <div key={userId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="font-medium">{data.name}</span>
              <span className="text-blue-600 font-semibold">{formatTime(data.minutes)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Time Entries Table */}
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        <div className="p-4 border-b">
          <h3 className="font-semibold">Time Entries</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Time</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">End Time</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                {isPartner && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredTasks.flatMap(task =>
                task.time_entries.map((entry, idx) => (
                  <tr key={`${task.id}-${idx}`} className={`hover:bg-gray-50 ${!entry.end_time ? 'bg-green-50' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{task.task_type_name}</div>
                      <div className="text-xs text-gray-500">{task.task_code}</div>
                    </td>
                    <td className="px-4 py-3">{task.client_name}</td>
                    <td className="px-4 py-3">{entry.user_name}</td>
                    <td className="px-4 py-3 text-sm">{formatDateTime(entry.start_time)}</td>
                    <td className="px-4 py-3 text-sm">
                      {entry.end_time ? formatDateTime(entry.end_time) : (
                        <span className="text-green-600 font-medium">In Progress</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-semibold ${!entry.end_time ? 'text-green-600' : 'text-blue-600'}`}>
                        {formatTime(getEntryDuration(entry))}
                        {!entry.end_time && <span className="ml-1 animate-pulse">●</span>}
                      </span>
                    </td>
                    {isPartner && (
                      <td className="px-4 py-3">
                        {!entry.end_time && (
                          <button
                            onClick={() => handleForceStop(task.id, entry.user_name)}
                            className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 flex items-center"
                            data-testid="force-stop-time-entry"
                          >
                            <StopCircle className="w-4 h-4 mr-1" />
                            Force Stop
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
              {filteredTasks.length === 0 && (
                <tr>
                  <td colSpan={isPartner ? "7" : "6"} className="px-4 py-8 text-center text-gray-500">
                    <Clock className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>No time entries found.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TimeTracking;
